import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Store } from '../types';

interface StoreContextType {
  currentStore: Store | null;
  setCurrentStore: (store: Store | null) => void;
  stores: Store[];
  setStores: (stores: Store[]) => void;
  initializeStore: () => void;
  clearStore: () => void;
}

const StoreContext = createContext<StoreContextType | undefined>(undefined);

interface StoreProviderProps {
  children: ReactNode;
}

export const StoreProvider: React.FC<StoreProviderProps> = ({ children }) => {
  const [currentStore, setCurrentStore] = useState<Store | null>(null);
  const [stores, setStores] = useState<Store[]>([]);

  // Initialize store from localStorage on mount
  const initializeStore = () => {
    const storedStore = localStorage.getItem('storeData');
    if (storedStore) {
      try {
        const parsedStore = JSON.parse(storedStore);
        setCurrentStore(parsedStore);
        setStores([parsedStore]);
      } catch (error) {
        // Invalid JSON in localStorage, clear it
        localStorage.removeItem('storeData');
      }
    }
  };

  const clearStore = () => {
    setCurrentStore(null);
    setStores([]);
    localStorage.removeItem('storeData');
  };

  // Initialize store on component mount
  useEffect(() => {
    initializeStore();
  }, []);

  const value: StoreContextType = {
    currentStore,
    setCurrentStore,
    stores,
    setStores,
    initializeStore,
    clearStore,
  };

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
};

export const useStore = (): StoreContextType => {
  const context = useContext(StoreContext);
  if (context === undefined) {
    throw new Error('useStore must be used within a StoreProvider');
  }
  return context;
}; 